import { useContext, useMemo, useState } from "react";
import { ObjectSchema, mixed, number, object, string } from "yup";
import { AvisConstants } from "../../../../../data/avisConstants";
import {
  ChildLicenseFilter,
  ChildLicenseObjectBriefInfo,
} from "../../../../../generated/data-contracts";
import { useSessionStorage } from "../../../../../hooks/useSessionStorage";
import { FilterType } from "../../../../../models";
import {
  IAxiosError,
  LicenseesUtils,
  TableUtils,
  api,
} from "../../../../../utils";
import { AvisSection } from "../../../../shared/components/form";
import {
  AvisFilterableTable,
  IColumnConfig,
  ITableColumnProps,
} from "../../../../shared/components/table";
import { AvisContext } from "../../../../shared/context/AvisContext";
import { ErrorLoadingDataHoc } from "../../../../shared/error/ErrorLoadingDataHoc";
import { ChildLicensesFilterForm } from "./ChildLicensesFilterForm";

export function GroupedLicensesGrid() {
  const { res } = useContext(AvisContext);
  const [filterStored] = useSessionStorage(
    `${AvisConstants.licenses_filter_values_key}${AvisConstants.grid_filter_suffix}`,
  );
  const [error, setError] = useState<IAxiosError>();

  const defaultFilterValues: ChildLicenseFilter = {
    licenseObjectName: filterStored ? filterStored.licenseObjectName : "",
    licenseObjectNameFilterType: filterStored
      ? filterStored.licenseObjectNameFilterType
      : "Contains",
  };

  const validationSchema: ObjectSchema<ChildLicenseFilter> = object({
    licenseObjectName: string(),
    licenseObjectNameFilterType: mixed<FilterType>(),
    pageIndex: number().min(0),
    pageSize: number().min(1),
  });

  const childLicenseObjectColumnTemplate = (
    rowData: ChildLicenseObjectBriefInfo,
    column: ITableColumnProps,
  ): JSX.Element => {
    if (!column.field || !rowData.licenseHeaderId) return <></>;

    if (
      !rowData.objects ||
      !Array.isArray(rowData.objects) ||
      rowData.objects.length === 0
    )
      return <></>;

    const objectsCollection = rowData.objects.map((x, i) => {
      const objectValue = LicenseesUtils.getLicenseObjectValue(x);
      return (
        <div
          key={`lid_${rowData.licenseHeaderId}_object_${i}`}
          className="license-object-record table-cell-value-overflow"
          title={objectValue}
        >
          {objectValue}
        </div>
      );
    });

    return <div>{objectsCollection}</div>;
  };
  const columnMetadata: IColumnConfig[] = useMemo(() => {
    return [
      TableUtils.columnConfig<ChildLicenseObjectBriefInfo>(
        "objects",
        res("licensees_License_label_ObjectName"),
        "Object",
        childLicenseObjectColumnTemplate,
      ),
      TableUtils.columnConfigPeriod<ChildLicenseObjectBriefInfo>(
        "dateFrom",
        "dateTo",
        res("licensees_License_label_Period"),
      ),
    ];
  }, [res]);

  const licenseId = LicenseesUtils.getUrlParams().licenseId;

  if (!licenseId) return;

  return (
    <AvisSection
      title={res("licensees_License_label_LicenseObjects")}
      textToDisplayKey="portal_LIC_GroupingLicenseObjects"
    >
      <ErrorLoadingDataHoc error={error}>
        <AvisFilterableTable<ChildLicenseObjectBriefInfo, ChildLicenseFilter>
          defaultFilterValues={defaultFilterValues}
          filterSessionKey={AvisConstants.licenses_child_filter_values_key}
          filterValidationSchema={validationSchema}
          setError={setError}
          getApiData={(filter) =>
            api.licenseesGetChildLicenses(licenseId, filter as any)
          }
          renderFilter={(bag, loading, filtered) => (
            <ChildLicensesFilterForm
              bag={bag}
              loading={loading}
              filtered={filtered}
            />
          )}
          tableColumns={columnMetadata}
          tableColumnWidths={[60, 40]}
        />
      </ErrorLoadingDataHoc>
    </AvisSection>
  );
}
